import React, { useState, useEffect, useMemo } from 'react';
import { 
  Link,
  Copy,
  Check,
  Search,
  Package,
  DollarSign,
  ExternalLink,
  QrCode,
  Share2,
  Download,
  Eye,
  Filter,
  RefreshCw
} from 'lucide-react';
import { ToastContext } from '../ToastProvider';
import { fetchStoreProductsPage } from '../../lib/storeProducts';
import { STORE_TYPES } from '../../lib/storeManagement';
import { getCooperativeStoreId } from '../../lib/productStoreUtils';

export default function ProductLinkGenerator({ affiliateId, referralCode, userId }) {
  const { notify } = React.useContext(ToastContext);
  
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showQrCode, setShowQrCode] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  // Default affiliate settings for products that don't have specific settings
  const defaultAffiliateSettings = {
    enabled: true,
    baseCommissionRate: 5.0,
    minOrderValue: 100.00
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // Debounce search term to prevent flickering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      // Get cooperative store ID
      const cooperativeStoreId = await getCooperativeStoreId();
      
      // Fetch cooperative store products
      const { items } = await fetchStoreProductsPage({ 
        storeType: STORE_TYPES.COOPERATIVE,
        pageSize: 100 
      });
      
      // Helper function to get default image based on product category/name
      const getDefaultProductImage = (product) => {
        const name = product.name?.toLowerCase() || '';
        const categories = product.categories || [];
        
        // Meat products
        if (name.includes('chicken') || name.includes('beef') || name.includes('pork') || 
            name.includes('hotdog') || name.includes('ham') || name.includes('chorizo') ||
            categories.some(cat => cat.toLowerCase().includes('meat'))) {
          return 'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=400&h=300&fit=crop&crop=center';
        }
        
        // Vegetables
        if (name.includes('vegetable') || name.includes('kangkong') || name.includes('pechay') ||
            name.includes('okra') || name.includes('radish') || name.includes('patola') ||
            name.includes('sigarilyas') || name.includes('upo') || name.includes('pipino') ||
            name.includes('sili') || name.includes('luya') || name.includes('beans') ||
            name.includes('carrots') || name.includes('cabbage') || name.includes('bawang') ||
            name.includes('sibuyas') || name.includes('kamatis') || name.includes('sitaw') ||
            name.includes('ampalaya') || name.includes('talong') || name.includes('kalabasa') ||
            categories.some(cat => cat.toLowerCase().includes('vegetable'))) {
          return 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400&h=300&fit=crop&crop=center';
        }
        
        // Fish/Seafood
        if (name.includes('fish') || name.includes('lapu-lapu') || name.includes('galunggong') ||
            name.includes('tuna') || categories.some(cat => cat.toLowerCase().includes('fish'))) {
          return 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop&crop=center';
        }
        
        // Rice/Grains
        if (name.includes('rice') || name.includes('monggo') || categories.some(cat => cat.toLowerCase().includes('rice'))) {
          return 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&crop=center';
        }
        
        // Condiments/Sauces
        if (name.includes('soy') || name.includes('vinegar') || name.includes('patis') ||
            name.includes('magic') || name.includes('knorr') || name.includes('ajinomoto') ||
            name.includes('datu') || name.includes('sinigang') || name.includes('mantika') ||
            categories.some(cat => cat.toLowerCase().includes('condiment'))) {
          return 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&crop=center';
        }
        
        // Dairy/Eggs
        if (name.includes('milk') || name.includes('bear brand') || name.includes('margarine') ||
            categories.some(cat => cat.toLowerCase().includes('dairy'))) {
          return 'https://images.unsplash.com/photo-1550583724-b2692b85b150?w=400&h=300&fit=crop&crop=center';
        }
        
        // Fruits
        if (name.includes('banana') || name.includes('lakatan') || name.includes('calamansi') ||
            name.includes('langka') || categories.some(cat => cat.toLowerCase().includes('fruit'))) {
          return 'https://images.unsplash.com/photo-1619546813926-a78fa6372cd2?w=400&h=300&fit=crop&crop=center';
        }
        
        // Default grocery image
        return 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&crop=center';
      };

      // Helper function to find the best available image URL
      const findProductImage = (product) => {
        // List of possible image field names to check
        const imageFields = [
          'image', 'images', 'imageUrl', 'image_url', 'photo', 'thumbnail', 'picture',
          'mainImage', 'main_image', 'productImage', 'product_image', 'imagePath',
          'imageLink', 'image_link', 'img', 'imgUrl', 'img_url', 'media', 'mediaUrl',
          'banner', 'cover', 'preview', 'displayImage', 'display_image'
        ];
        
        // Check each field for a valid image URL
        for (const field of imageFields) {
          const value = product[field];
          if (value) {
            // Handle array of images
            if (Array.isArray(value) && value.length > 0) {
              const firstImage = value[0];
              if (typeof firstImage === 'string' && firstImage.trim() !== '') {
                return firstImage;
              }
            }
            // Handle single image URL
            else if (typeof value === 'string' && value.trim() !== '') {
              return value;
            }
          }
        }
        
        // If no image found, return null (will use default)
        return null;
      };

      // Transform products to include affiliate settings and resolved images
      const productsWithAffiliateSettings = items.map(product => {
        const foundImage = findProductImage(product);
        
        return {
          ...product,
          image: foundImage || getDefaultProductImage(product), // Use found image or default
          affiliateSettings: {
            enabled: product.affiliateEnabled !== false, // Default to enabled unless explicitly disabled
            baseCommissionRate: product.affiliateCommissionRate || defaultAffiliateSettings.baseCommissionRate,
            minOrderValue: product.affiliateMinOrderValue || defaultAffiliateSettings.minOrderValue
          }
        };
      });
      
      console.log('Loaded products:', productsWithAffiliateSettings.slice(0, 3)); // Log first 3 products for debugging
      console.log('Sample product structure:', productsWithAffiliateSettings[0]); // Log full structure of first product
      console.log('All product fields:', Object.keys(productsWithAffiliateSettings[0])); // Log all available fields
      setProducts(productsWithAffiliateSettings);
    } catch (error) {
      console.error('Error loading products:', error);
      notify('Failed to load products', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    if (!debouncedSearchTerm) return products;
    
    return products.filter(product => 
      product.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      product.categories?.some(cat => cat.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
      product.sku?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [products, debouncedSearchTerm]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const generateAffiliateLink = (product) => {
    const baseUrl = window.location.origin;
    const productId = product.id;
    
    // Generate affiliate link with tracking parameters
    // Use referralCode for BOTH affiliate and ref parameters
    const affiliateLink = `${baseUrl}/store?affiliate=${referralCode}&product=${productId}&ref=${referralCode}`;
    
    setGeneratedLink(affiliateLink);
    setSelectedProduct(product);
    notify('Affiliate link generated successfully!', { type: 'success' });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopiedLink(true);
      notify('Link copied to clipboard!', { type: 'success' });
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      notify('Failed to copy link', { type: 'error' });
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Check out ${selectedProduct.name}`,
          text: `I found this great product: ${selectedProduct.name}`,
          url: generatedLink
        });
        notify('Link shared successfully!', { type: 'success' });
      } catch (error) {
        console.error('Failed to share:', error);
        notify('Failed to share link', { type: 'error' });
      }
    } else {
      // Fallback to copying
      copyToClipboard();
    }
  };

  const downloadQrCode = () => {
    // Here you would implement QR code generation and download
    notify('QR code download feature coming soon!', { type: 'info' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
        </div>

        {/* Search Skeleton */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
        </div>

        {/* Products Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="h-48 bg-gray-200 animate-pulse"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4"></div>
                <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Link Generator</h1>
          <p className="text-gray-600">Create affiliate links for specific products</p>
        </div>
        <button
          onClick={loadProducts}
          className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="text-sm text-gray-600">
            {filteredProducts.length} products found
          </div>
        </div>
      </div>

      {/* Generated Link Display */}
      {generatedLink && selectedProduct && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Generated Affiliate Link</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowQrCode(!showQrCode)}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-1"
              >
                <QrCode className="w-4 h-4" />
                <span>QR Code</span>
              </button>
              <button
                onClick={downloadQrCode}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center space-x-1"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {/* Product Info */}
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <img
                src={selectedProduct.image}
                alt={selectedProduct.name}
                className="w-16 h-16 object-cover rounded-lg"
                onError={(e) => {
                  console.log('Generated link image failed to load:', selectedProduct.image);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                onLoad={() => {
                  console.log('Generated link image loaded successfully:', selectedProduct.image);
                }}
              />
              <div 
                className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center hidden"
              >
                <Package className="w-6 h-6 text-gray-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{selectedProduct.name}</h4>
                <p className="text-sm text-gray-600">{selectedProduct.categories?.[0] || 'Uncategorized'}</p>
                <p className="text-sm font-medium text-green-600">
                  {formatCurrency(selectedProduct.price)} • {selectedProduct.affiliateSettings.baseCommissionRate}% commission
                </p>
              </div>
            </div>

            {/* Generated Link */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Affiliate Link</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={generatedLink}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
                >
                  {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copiedLink ? 'Copied!' : 'Copy'}</span>
                </button>
                <button
                  onClick={shareLink}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Share</span>
                </button>
              </div>
            </div>

            {/* QR Code Display */}
            {showQrCode && (
              <div className="flex items-center justify-center p-4 bg-white border border-gray-200 rounded-lg">
                <div className="text-center">
                  <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                    <QrCode className="w-16 h-16 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600">QR Code for {selectedProduct.name}</p>
                </div>
              </div>
            )}

            {/* Link Preview */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Link Preview</label>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <ExternalLink className="w-4 h-4" />
                  <span>Clicking this link will track your affiliate commission</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map((product) => (
          <div key={product.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
            <div className="relative">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-48 object-cover"
                onError={(e) => {
                  console.log('Image failed to load:', product.image);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                onLoad={() => {
                  console.log('Image loaded successfully:', product.image);
                }}
              />
              <div 
                className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-500 hidden"
              >
                <div className="text-center">
                  <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <span className="text-sm text-gray-500">Image unavailable</span>
                </div>
              </div>
              {!product.affiliateSettings.enabled && (
                <div className="absolute top-2 right-2 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                  Disabled
                </div>
              )}
            </div>
            
            <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-900">{product.name}</h3>
              <span className="text-sm text-gray-500">{product.categories?.[0] || 'Uncategorized'}</span>
            </div>
              
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
              
              <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-semibold text-green-600">
                  {formatCurrency(product.price)}
                </div>
                <div className="text-sm text-gray-500">
                  {product.stock || product.quantity || 0} in stock
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Commission Rate:</span>
                  <span className="font-medium text-blue-600">
                    {product.affiliateSettings.baseCommissionRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Min Order:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(product.affiliateSettings.minOrderValue)}
                  </span>
                </div>
              </div>

              <button
                onClick={() => generateAffiliateLink(product)}
                disabled={!product.affiliateSettings.enabled}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  product.affiliateSettings.enabled
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {product.affiliateSettings.enabled ? 'Generate Link' : 'Affiliate Disabled'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-8">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No products found matching your search</p>
        </div>
      )}
    </div>
  );
}
